import { useEffect } from "react";

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (opts: { url: string }) => void;
    };
  }
}

/**
 * Opens Calendly's popup widget. Falls back to a new tab when `widget.js` has
 * not finished loading — the script is injected async, so an early click would
 * otherwise do nothing at all.
 *
 * The caller (or an ancestor) must have run `useCalendlyPopup()` to inject the
 * widget assets.
 */
export function openCalendlyPopup(url: string): void {
  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/** Lazily injects the Calendly popup widget assets once on mount. */
export function useCalendlyPopup(): typeof openCalendlyPopup {
  useEffect(() => {
    const cssId = "calendly-widget-css";
    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId;
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }
    const scriptId = "calendly-widget-js";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return openCalendlyPopup;
}
